import React, {useMemo} from "react";
import {useNavigate} from "react-router-dom";
import {CheckIcon, NoSymbolIcon} from "@heroicons/react/24/solid";
import {ContentStatus} from "../../../../constant/CodeSpecNum";
import {getStepToneStyle} from "../../../../components/stepper/stepperPalette";
import {formatSimmsDateTime} from "../../../../components/commonmethod/CommonMethod";
import {navigateWithChildTab, pickDetailTabTitle} from "../../../../components/pageTabs/pageTabsStorage";
import "../../../../components/stepper/stepper.css";

const COMPLETE_NUMERIC = ContentStatus.COMPLETE.statusNumericValue;
const ACTIVE_MIN = ContentStatus.APPROVE.statusNumericValue;

function resolveInspectionStepState(status) {
    if (!status) return "pending";
    if (status.rejected === ContentStatus.REJECT.statusValue) return "rejected";
    const value = Number(status.statusNumericValue) || 0;
    if (value >= COMPLETE_NUMERIC) return "done";
    if (value >= ACTIVE_MIN) return "active";
    if (value <= ContentStatus.PRE_INSERT.statusNumericValue) return "pending";
    return "early";
}

function resolveStepStatusLabel(inspection, index, state) {
    if (state === "done") return "완료";
    if (state === "rejected") return "반려";
    return inspection.contentStatus?.statusKorValue ?? `${index + 1}회`;
}

function formatInspectionDateTooltip(inspection) {
    const start = formatSimmsDateTime(inspection?.inspectStartDate);
    const end = formatSimmsDateTime(inspection?.inspectEndDate);
    if (start && end) return `${start} ~ ${end}`;
    if (start) return `${start} ~`;
    if (end) return `~ ${end}`;
    return "";
}

function InspectionStepDates({inspection}) {
    const start = formatSimmsDateTime(inspection?.inspectStartDate);
    const end = formatSimmsDateTime(inspection?.inspectEndDate);

    if (!start && !end) return null;

    return (
        <span className="simms-stepper-step__dates">
            {start ? <span className="simms-stepper-step__date-line">{start}</span> : null}
            {end ? (
                <span className="simms-stepper-step__date-line">
                    {start ? `~ ${end}` : end}
                </span>
            ) : start ? (
                <span className="simms-stepper-step__date-line">~</span>
            ) : null}
        </span>
    );
}

function StepIcon({state, order}) {
    if (state === "done") {
        return <CheckIcon className="h-3.5 w-3.5" aria-hidden />;
    }
    if (state === "rejected") {
        return <NoSymbolIcon className="h-3.5 w-3.5" aria-hidden />;
    }
    return <span>{order}</span>;
}

function sortInspections(list) {
    return [...list].sort((a, b) => {
        const aDate = a?.inspectStartDate ?? a?.createdAt ?? "";
        const bDate = b?.inspectStartDate ?? b?.createdAt ?? "";
        if (aDate === bDate) return 0;
        return aDate < bDate ? -1 : 1;
    });
}

function summarizeInspections(inspections) {
    let done = 0;
    let active = 0;
    let pending = 0;
    let rejected = 0;

    inspections.forEach((item) => {
        const state = resolveInspectionStepState(item?.contentStatus);
        if (state === "done") done += 1;
        else if (state === "rejected") rejected += 1;
        else if (state === "pending") pending += 1;
        else active += 1;
    });

    const total = inspections.length;
    const percent = total > 0 ? Math.min(Math.round((done / total) * 100), 100) : 0;

    return {done, active, pending, rejected, total, percent};
}

function InspectionHeaderProgressTrack({inspections = []}) {
    const navigate = useNavigate();

    const sorted = useMemo(() => sortInspections(inspections), [inspections]);
    const summary = useMemo(() => summarizeInspections(sorted), [sorted]);

    const handleStepClick = (inspection) => {
        const rowId = inspection?.id;
        if (rowId == null) return;
        navigateWithChildTab(navigate, {
            title: pickDetailTabTitle(inspection, "점검 상세"),
            url: `/maintenance/inspection/${rowId}`,
        });
    };

    if (sorted.length === 0) {
        return (
            <section className="detail-progress-status border-b border-[#e5e7eb]" aria-label="점검 진행 현황">
                <p className="text-[12px] text-[#64748b]">생성된 점검 건이 없습니다.</p>
            </section>
        );
    }

    return (
        <section className="detail-progress-status border-b border-[#e5e7eb]" aria-label="점검 진행 현황">
            <div className="mb-2.5 flex min-w-0 flex-wrap items-center justify-between gap-x-3 gap-y-1">
                <span className="text-[13px] font-semibold text-[#334155]">점검 진행 현황</span>
                <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-0.5">
                    <span className="text-[12px] font-medium tabular-nums text-[#133E87]">
                        완료 {summary.done}/{summary.total} ({summary.percent}%)
                    </span>
                    {sorted.length > 4 ? (
                        <span className="text-[10px] text-[#94a3b8]">좌우 스크롤</span>
                    ) : null}
                </div>
            </div>

            <div className="simms-stepper simms-stepper--inspection-plan">
                <div className="simms-stepper-track is-scroll" role="list">
                    {sorted.map((inspection, index) => {
                        const state = resolveInspectionStepState(inspection?.contentStatus);
                        const statusLabel = resolveStepStatusLabel(inspection, index, state);
                        const dateRange = formatInspectionDateTooltip(inspection);
                        const stateClass = state === "early" ? "is-early is-active" : `is-${state}`;
                        const title = [
                            inspection?.title,
                            dateRange,
                            statusLabel,
                        ].filter(Boolean).join(" · ");

                        return (
                            <button
                                key={inspection.id ?? index}
                                type="button"
                                role="listitem"
                                className={`simms-stepper-step simms-stepper-step--with-dates ${stateClass} ${
                                    index === 0 ? "is-first" : ""
                                } ${index === sorted.length - 1 ? "is-last" : ""}`}
                                style={getStepToneStyle(index)}
                                title={title}
                                onClick={() => handleStepClick(inspection)}
                            >
                                <span className="simms-stepper-step__order">
                                    <StepIcon state={state} order={index + 1} />
                                </span>
                                <span className="simms-stepper-step__text">
                                    <span className="simms-stepper-step__label">{statusLabel}</span>
                                    <InspectionStepDates inspection={inspection} />
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>

            <div className="mt-2 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-[#64748b]">
                <span>완료 {summary.done}</span>
                <span>진행 {summary.active}</span>
                <span>대기 {summary.pending}</span>
                {summary.rejected > 0 ? <span className="text-[#c43636]">반려 {summary.rejected}</span> : null}
            </div>
        </section>
    );
}

export {InspectionHeaderProgressTrack};
